import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import baseURL from '../component/baseURL';
import showNotification from '../component/Notification';
import stringsoflanguages from '../component/stringsoflanguages';

/** Turn API labels like StandardStatus → "Standard Status" */
function formatFieldLabel(label) {
    if (!label || typeof label !== 'string') return '';
    if (label.includes('/') || label.includes('(')) return label;
    return label
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .trim();
}

function normalizeAddressList(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && typeof payload === 'object') return Object.values(payload);
    return [];
}

function formatPropertyTypeDisplay(typeStr) {
    const s = String(typeStr ?? '').trim();
    if (!s) return '';
    // Split by spaces; keep '/' as a token.
    return s
        .split(' ')
        .map((w) => {
            if (w === '/') return '/';
            const lw = w.toLowerCase();
            return lw.charAt(0).toUpperCase() + lw.slice(1);
        })
        .join(' ');
}

/** Target max size per image after optimization (bytes). */
const MAX_LISTING_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_LISTING_IMAGE_COUNT = 10;

const ALLOWED_IMAGE_MIME = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']);

function isAllowedListingImageFile(file) {
    const type = (file.type || '').toLowerCase();
    if (ALLOWED_IMAGE_MIME.has(type)) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(jpe?g|png|gif|webp)$/i.test(name);
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve) => {
        canvas.toBlob((b) => resolve(b), type, quality);
    });
}

const WEBP_SUPPORTED =
    typeof document !== 'undefined' &&
    (() => {
        try {
            const c = document.createElement('canvas');
            return c.toDataURL('image/webp').startsWith('data:image/webp');
        } catch {
            return false;
        }
    })();

/**
 * Re-encode images larger than maxBytes (canvas → JPEG, then WebP if needed) until under the limit.
 * GIFs use the first frame only; transparency is flattened onto white.
 */
async function optimizeListingImage(file, maxBytes) {
    if (file.size <= maxBytes) {
        return file;
    }

    let bitmap;
    try {
        bitmap = await createImageBitmap(file);
    } catch {
        throw new Error('optimize');
    }

    const w = bitmap.width;
    const h = bitmap.height;
    if (!w || !h) {
        bitmap.close();
        throw new Error('optimize');
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        bitmap.close();
        throw new Error('optimize');
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const maxDimension = 4096;
    let scale = Math.min(1, maxDimension / Math.max(w, h));

    const drawAndBlob = async (mime, q) => {
        const sw = Math.max(1, Math.round(w * scale));
        const sh = Math.max(1, Math.round(h * scale));
        canvas.width = sw;
        canvas.height = sh;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sw, sh);
        ctx.drawImage(bitmap, 0, 0, sw, sh);
        return canvasToBlob(canvas, mime, q);
    };

    const squeeze = async (mime, initialQ) => {
        let q = initialQ;
        let blob = await drawAndBlob(mime, q);
        let attempts = 0;
        while (blob && blob.size > maxBytes && attempts < 120) {
            attempts += 1;
            if (q > 0.42) {
                q -= 0.04;
            } else if (scale > 0.1) {
                scale *= 0.87;
                q = initialQ;
            } else {
                break;
            }
            blob = await drawAndBlob(mime, q);
        }
        return blob;
    };

    let blob = await squeeze('image/jpeg', 0.9);
    if ((!blob || blob.size > maxBytes) && WEBP_SUPPORTED) {
        scale = Math.min(1, maxDimension / Math.max(w, h));
        blob = await squeeze('image/webp', 0.88);
    }

    bitmap.close();

    if (!blob || blob.size > maxBytes) {
        throw new Error('optimize');
    }

    const base = (file.name || 'image').replace(/\.[^.]+$/, '');
    const ext = blob.type === 'image/webp' ? '.webp' : '.jpg';
    return new File([blob], `${base}${ext}`, { type: blob.type, lastModified: Date.now() });
}

const STANDARD_STATUS_OPTIONS = [
    'Active',
    'Coming Soon',
    'Sold',
    'Nearly Sold',
    'Reserve',
    'Under Offer',
    'Inactive',
];

/** AI public-remarks language options (shortform sent as `ai_lang`) */
const langArray = [
    { shortform: 'en', longform: 'English', lanName: 'English' },
    { shortform: 'fr', longform: 'French', lanName: 'français' },
    { shortform: 'nl', longform: 'Dutch', lanName: 'Nederlands' },
    { shortform: 'uk', longform: 'Ukrainian', lanName: 'український' },
    { shortform: 'de', longform: 'German', lanName: 'Deutsch' },
    { shortform: 'pl', longform: 'Polish', lanName: 'Polski' },
    { shortform: 'ro', longform: 'Romanian', lanName: 'Română' },
    { shortform: 'tr', longform: 'Turkish', lanName: 'Türkçe' },
    { shortform: 'pt', longform: 'Portuguese', lanName: 'Português' },
];

export default function AddListing() {
    const navigate = useNavigate();
    const imageInputRef = useRef(null);
    /** Mobile: opens device camera via capture attribute */
    const cameraInputRef = useRef(null);
    const remarksLangMenuRef = useRef(null);

    const [listingType, setListingType] = useState('public');
    const [feedGroup, setFeedGroup] = useState('1');
    const [remarksMode, setRemarksMode] = useState('ai');
    /** Each item: { file, url } — single state keeps files and previews in sync */
    const [listingImages, setListingImages] = useState([]);
    const [imageDropActive, setImageDropActive] = useState(false);
    const listingImagesRef = useRef(listingImages);
    listingImagesRef.current = listingImages;
    const [listingFieldIds, setListingFieldIds] = useState([]);
    const [propertyTypeOptions, setPropertyTypeOptions] = useState([]);
    const [loadData, setLoadData] = useState(false);
    const [fieldValues, setFieldValues] = useState({});
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isImageUploading, setIsImageUploading] = useState(false);
    const [isOptimizingImages, setIsOptimizingImages] = useState(false);
    const isOptimizingImagesRef = useRef(false);
    const [newListingId, setNewListingId] = useState('');
    const [userID, setUserID] = useState('');
    const [aiRemarksLoading, setAiRemarksLoading] = useState(false);
    const [countryList, setCountryList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [cityList, setCityList] = useState([]);
    /** Codes/ids for cascading APIs; fieldValues holds display names for listing payload */
    const [addressCountryCode, setAddressCountryCode] = useState('');
    const [addressStateId, setAddressStateId] = useState('');
    const [addressCityId, setAddressCityId] = useState('');
    const [selectedAiLang, setSelectedAiLang] = useState('en');
    const [remarksLangMenuOpen, setRemarksLangMenuOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    /** Mobile: show Gallery / Camera only after user taps "Add Image" */
    const [mobileImagePickerOpen, setMobileImagePickerOpen] = useState(false);
    /** Remount camera input so iOS/Android reliably fire change on each new photo */
    const [cameraInputKey, setCameraInputKey] = useState(0);

    const setField = useCallback((fieldName, value) => {
        setFieldValues((prev) => ({ ...prev, [fieldName]: value }));
    }, []);

    const fetchCountryAPI = useCallback(() => {
        fetch(baseURL.URL + '/address/country', { method: 'POST' })
            .then((response) => response.json())
            .then((responseJson) => {
                setCountryList(responseJson.response?.countries || []);
            })
            .catch((error) => {
                console.error('Error fetching countries:', error);
                setCountryList([]);
            });
    }, []);

    const fetchStateAPI = useCallback((countryCode) => {
        if (!countryCode) {
            setStateList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append('country_code', countryCode);
        fetch(baseURL.URL + '/address/state', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const statesPayload = responseJson.response?.states ?? responseJson.response?.regions ?? responseJson.response;
                setStateList(normalizeAddressList(statesPayload));
            })
            .catch((error) => {
                console.error('Error fetching states:', error);
                setStateList([]);
            });
    }, []);

    const fetchCityAPI = useCallback((stateId, countryCode) => {
        if (!stateId || !countryCode) {
            setCityList([]);
            return;
        }
        const formdata = new FormData();
        formdata.append('country_code', countryCode);
        formdata.append('state_id', stateId);
        fetch(baseURL.URL + '/address/city', { method: 'POST', body: formdata })
            .then((response) => response.json())
            .then((responseJson) => {
                const citiesPayload = responseJson.response?.cities ?? responseJson.response;
                setCityList(normalizeAddressList(citiesPayload));
            })
            .catch((error) => {
                console.error('Error fetching cities:', error);
                setCityList([]);
            });
    }, []);

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        if (!userid) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }
        setUserID(userid);
        beforeAddListingFetch(userid);
    }, []);

    useEffect(() => {
        fetchCountryAPI();
    }, [fetchCountryAPI]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (listingType === 'pocket') setFeedGroup('6');
    }, [listingType]);

    useEffect(() => {
        if (!remarksLangMenuOpen) return;
        const onDocMouseDown = (e) => {
            if (remarksLangMenuRef.current && !remarksLangMenuRef.current.contains(e.target)) {
                setRemarksLangMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [remarksLangMenuOpen]);

    useEffect(() => {
        return () => {
            listingImagesRef.current.forEach((item) => URL.revokeObjectURL(item.url));
        };
    }, []);

    const ingestListingImages = useCallback(async (picked) => {
        const files = Array.from(picked || []).filter((f) => f && f.size !== undefined);
        if (!files.length) return;
        if (isOptimizingImagesRef.current) return;

        const prevSnapshot = listingImagesRef.current;
        const room = MAX_LISTING_IMAGE_COUNT - prevSnapshot.length;
        if (room <= 0) {
            showNotification('Error', `You can upload at most ${MAX_LISTING_IMAGE_COUNT} images.`, 'danger');
            return;
        }

        const badType = [];
        const valid = [];
        files.forEach((file) => {
            if (!isAllowedListingImageFile(file)) {
                badType.push(file.name || 'file');
            } else {
                valid.push(file);
            }
        });

        if (badType.length) {
            const sample = badType.slice(0, 3).join(', ');
            showNotification(
                'Error',
                badType.length === 1
                    ? `"${badType[0]}" is not a supported format. Use JPG, PNG, GIF, or WebP.`
                    : `Some files are not supported (${sample}${badType.length > 3 ? '…' : ''}). Use JPG, PNG, GIF, or WebP.`,
                'danger'
            );
        }

        const toProcess = valid.slice(0, room);
        if (valid.length > room) {
            showNotification(
                'Error',
                `Only ${room} more image slot(s) available (max ${MAX_LISTING_IMAGE_COUNT} total).`,
                'danger'
            );
        }

        if (!toProcess.length) return;

        isOptimizingImagesRef.current = true;
        setIsOptimizingImages(true);
        try {
            const optimized = [];
            for (const file of toProcess) {
                try {
                    const out = await optimizeListingImage(file, MAX_LISTING_IMAGE_BYTES);
                    optimized.push(out);
                } catch {
                    showNotification(
                        'Error',
                        `"${file.name || 'Image'}" could not be compressed under 5 MB. Try a smaller image.`,
                        'danger'
                    );
                }
            }
            if (!optimized.length) return;

            setListingImages((prev) => {
                const roomLeft = MAX_LISTING_IMAGE_COUNT - prev.length;
                const add = optimized.slice(0, roomLeft);
                if (!add.length) return prev;
                const newEntries = add.map((file) => ({
                    file,
                    url: URL.createObjectURL(file),
                }));
                return [...prev, ...newEntries];
            });
        } finally {
            isOptimizingImagesRef.current = false;
            setIsOptimizingImages(false);
        }
    }, []);

    const handleAddImageClick = () => {
        if (isOptimizingImagesRef.current || isImageUploading) return;
        imageInputRef.current?.click();
    };

    const handleMobileCameraClick = () => {
        if (isOptimizingImagesRef.current || isImageUploading) return;
        cameraInputRef.current?.click();
    };

    const handleImageChange = (e) => {
        const input = e.target;
        const picked = Array.from(input.files || []);
        input.value = '';
        ingestListingImages(picked);
    };

    /** Camera capture: ingest synchronously, then remount input so the next shot works on iOS/Android */
    const handleCameraImageChange = (e) => {
        const input = e.target;
        const files = input.files;
        if (!files?.length) {
            return;
        }
        const picked = Array.from(files);
        ingestListingImages(picked);
        input.value = '';
        setTimeout(() => setCameraInputKey((k) => k + 1), 150);
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDropActive(false);
        if (isOptimizingImagesRef.current || isImageUploading) return;
        ingestListingImages(e.dataTransfer?.files);
    };

    const handleImageDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setImageDropActive(true);
    };

    const handleImageDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget)) setImageDropActive(false);
    };

    useEffect(() => {
        const onPaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items?.length) return;
            const files = [];
            for (let i = 0; i < items.length; i += 1) {
                const it = items[i];
                if (it.kind === 'file') {
                    const f = it.getAsFile();
                    if (f) files.push(f);
                }
            }
            if (!files.length) return;
            if (isOptimizingImagesRef.current || isImageUploading) return;
            e.preventDefault();
            ingestListingImages(files);
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [ingestListingImages, isImageUploading]);

    const handleImageCancel = () => {
        setListingImages((prev) => {
            prev.forEach((item) => URL.revokeObjectURL(item.url));
            return [];
        });
    };

    const removeListingImage = useCallback((url) => {
        setListingImages((prev) => {
            const removed = prev.find((item) => item.url === url);
            if (removed) URL.revokeObjectURL(removed.url);
            return prev.filter((item) => item.url !== url);
        });
    }, []);

    const handleSave = () => {
        navigate('/home/my-listings');
    };


    function beforeAddListingFetch(userid) {
        const formdata = new FormData();
        formdata.append('user_id', userid);
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        fetch(baseURL.URL + '/portfolio/before_add_listing_fetch_fields', {
            method: 'POST',
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server.code !== 200) {
                    showNotification('Error', responseJson.server.message, 'danger');
                    return;
                }
                const containers = responseJson.response?.listing_container_array1;
                const first = Array.isArray(containers) ? containers[0] : null;
                const fields = first?.listing_field_ids;
                if (!Array.isArray(fields) || fields.length === 0) {
                    showNotification('Error', 'No listing fields configured.', 'danger');
                    setLoadData(true);
                    return;
                }
                const propertyTypes = responseJson.response?.property_type;
                setPropertyTypeOptions(Array.isArray(propertyTypes) ? propertyTypes : []);
                setListingFieldIds(fields);
                const initial = {};
                fields.forEach((f) => {
                    if (f?.field_name) initial[f.field_name] = '';
                });
                setFieldValues(initial);
                setLoadData(true);
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', 'Could not load listing fields.', 'danger');
                setLoadData(true);
            });
    }

    async function addListingApi() {
        if (!userID) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }

        const hiddenFields = new Set(['Basement', 'Appliances', 'ArchitecturalStyle']);
        const missingRequired = listingFieldIds.find((f) => {
            if (!f?.field_name || Number(f.validationRequired) !== 1) return false;
            if (hiddenFields.has(f.field_name)) return false;
            const value = fieldValues[f.field_name];
            return value === undefined || value === null || String(value).trim() === '';
        });

        if (missingRequired) {
            showNotification(
                'Error',
                `Please fill ${formatFieldLabel(missingRequired.field_name_label || missingRequired.field_name)}`,
                'danger'
            );
            return;
        }

        try {
            setIsDataLoading(true);
            const payloadFieldValues = {
                ...fieldValues,
            };
            const normalizeHelperText = (value) =>
                typeof value === 'string' ? value.replace(/(\r\n|\n|\r)/gm, '').trim() : value;
            payloadFieldValues.country_name = normalizeHelperText(payloadFieldValues.country_name);
            payloadFieldValues.state_name = normalizeHelperText(payloadFieldValues.state_name);
            payloadFieldValues.city_name = normalizeHelperText(payloadFieldValues.city_name);
            // UI stores `PropertyType` as id; backend expects a readable string.
            if (payloadFieldValues.PropertyType) {
                const matched = propertyTypeOptions.find(
                    (pt) => String(pt.id) === String(payloadFieldValues.PropertyType)
                );
                payloadFieldValues.PropertyType = formatPropertyTypeDisplay(
                    matched?.type ?? payloadFieldValues.PropertyType
                );
            }
            const formdata = new FormData();
            formdata.append('user_id', userID);
            formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
            formdata.append('listing_privacy', listingType === 'public' ? '1' : '0');
            formdata.append('feed_type', String(feedGroup || '1'));
            formdata.append('field', JSON.stringify(payloadFieldValues));

            const response = await fetch(baseURL.URL + '/portfolio/listing_add', {
                method: 'POST',
                body: formdata,
            });
            const responseJson = await response.json();
            const code = responseJson?.server?.result_code ?? responseJson?.server?.code;
            const message = responseJson?.server?.message || 'Listing save failed.';

            if (Number(code) === 200) {
                const listingId = String(responseJson?.response?.listing_id || '');
                setNewListingId(listingId);
                if (listingImages.length > 0) {
                    await updateProfileImage(listingId);
                }

                showNotification('Success', message, 'success');
                navigate('/home/my-listings');

                return;
            }

            showNotification('Error', message, 'danger');
        } catch (error) {
            console.error(error);
            showNotification('Error', 'Could not save listing.', 'danger');
        } finally {
            setIsDataLoading(false);
        }
    }

    async function updateProfileImage(listingIdParam) {
        if (!userID) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }
        const listingIdToUse = listingIdParam || newListingId || '';
        if (!listingIdToUse) {
            showNotification('Error', 'Please submit listing first, then save images.', 'danger');
            return;
        }
        if (!listingImages.length) {
            showNotification('Error', 'Please add at least one image.', 'danger');
            return;
        }

        const oversized = listingImages.map((x) => x.file).filter((f) => f.size > MAX_LISTING_IMAGE_BYTES);
        if (oversized.length) {
            showNotification(
                'Error',
                'One or more images exceed 5 MB. Remove them and add smaller files.',
                'danger'
            );
            return;
        }

        const toBase64DataUrl = (file) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });

        try {
            setIsImageUploading(true);
            for (let i = 0; i < listingImages.length; i += 1) {
                const b64 = await toBase64DataUrl(listingImages[i].file);
                const formdata = new FormData();
                formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
                formdata.append('user_id', userID);
                formdata.append('listing_id', listingIdToUse);
                formdata.append('album', b64);
                const response = await fetch(baseURL.URL + '/portfolio/attach_image_files', {
                    method: 'POST',
                    body: formdata,
                });
                const responseJson = await response.json();
                const code = responseJson?.server?.result_code ?? responseJson?.server?.code;
                if (Number(code) !== 200) {
                    throw new Error(responseJson?.server?.message || 'Failed to upload image');
                }
            }
            showNotification('Success', 'Listing images uploaded successfully', 'success');
            handleImageCancel();
        } catch (error) {
            console.error(error);
            showNotification('Error', error?.message || 'Failed to upload images', 'danger');
        } finally {
            setIsImageUploading(false);
        }
    }

    const aiGeneratedResponse = useCallback(async () => {
        if (!userID) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }

        const v = fieldValues;
        const missing = (label, val) => {
            if (val === undefined || val === null || String(val).trim() === '') {
                showNotification('Error', label, 'danger');
                return true;
            }
            return false;
        };

        if (missing('Please enter Street Number', v.StreetNumber)) return;
        if (missing('Please enter Street Name', v.StreetName)) return;
        if (missing('Please enter City', v.City)) return;
        if (missing('Please enter State Or Province', v.StateOrProvince)) return;
        if (missing('Please enter Country', v.Country)) return;
        if (missing('Please enter Postal Code', v.PostalCode)) return;
        if (missing('Please select Property Type', v.PropertyType)) return;
        if (missing('Please enter Bedrooms Total', v.BedroomsTotal)) return;
        if (missing('Please enter Bathrooms Total', v.BathroomsTotalInteger)) return;
        if (missing('Please enter Living Area', v.LivingArea)) return;
        if (missing('Please select Living Area Units', v.LivingAreaUnits)) return;

        const address = `${v.StreetNumber} ${v.StreetName}, ${v.city_name || v.City}, ${v.state_name || v.StateOrProvince} ${v.country_name || v.Country}, ${v.PostalCode}`;

        try {
            setAiRemarksLoading(true);
            const formdata = new FormData();
            formdata.append('user_id', userID);
            formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
            const matchedPT = propertyTypeOptions.find(
                (pt) => String(pt.id) === String(v.PropertyType)
            );
            formdata.append(
                'property_type',
                formatPropertyTypeDisplay(matchedPT?.type ?? v.PropertyType)
            );
            formdata.append('address', address);
            formdata.append('beds', String(v.BedroomsTotal));
            formdata.append('baths', String(v.BathroomsTotalInteger));
            formdata.append('garage', String(v.GarageSpaces || '0'));
            formdata.append('parking', String(v.ParkingTotal || '0'));
            formdata.append('livingarea', String(v.LivingArea));
            formdata.append('livingareaunit', String(v.LivingAreaUnits));
            formdata.append('ai_lang', selectedAiLang);

            const response = await fetch(baseURL.URL + '/portfolio/ai_generated_description', {
                method: 'POST',
                body: formdata,
            });
            const responseJson = await response.json();

            if (responseJson?.server?.code === 200) {
                const raw = responseJson.response?.result?.description ?? '';
                const description = String(raw).replace(/^\n+|\n+$/g, '');
                setField('PublicRemarks', description);
                showNotification('Success', responseJson.server.message || 'Description generated', 'success');
            } else {
                showNotification('Error', responseJson?.server?.message || 'Could not generate description', 'danger');
            }
        } catch (error) {
            console.error(error);
            showNotification('Error', 'Could not generate description', 'danger');
        } finally {
            setAiRemarksLoading(false);
        }
    }, [userID, fieldValues, setField, selectedAiLang]);

    const renderDynamicFields = () => {
        const fields = listingFieldIds;
        const out = [];
        let i = 0;
        const hiddenFields = new Set(['Basement', 'Appliances', 'ArchitecturalStyle']);

        while (i < fields.length) {
            const f = fields[i];
            const name = f.field_name;
            const required = Number(f.validationRequired) === 1;

            if (hiddenFields.has(name)) {
                i += 1;
                continue;
            }

            if (name === 'LivingArea') {
                const next = fields[i + 1];
                if (next?.field_name === 'LivingAreaUnits') {
                    const label = formatFieldLabel(f.field_name_label || 'BuildArea');
                    out.push(
                        <div key={`pair-living-${i}`} className="add-listing-field-block">
                            <span className="add-listing-field-label">
                                {label}
                                {required && <span className="add-listing-required-star">*</span>}
                            </span>
                            <div className="add-listing-inline-row">
                                <input
                                    type="text"
                                    className="add-listing-input add-listing-input--grow"
                                    placeholder={formatFieldLabel(f.field_name_label || 'LivingArea')}
                                    value={fieldValues.LivingArea ?? ''}
                                    onChange={(e) => setField('LivingArea', e.target.value)}
                                    required={required}
                                />
                                <select
                                    className="add-listing-input add-listing-unit-select"
                                    value={fieldValues.LivingAreaUnits ?? ''}
                                    onChange={(e) => setField('LivingAreaUnits', e.target.value)}
                                    aria-label={formatFieldLabel(next.field_name_label || 'Unit')}
                                    required={Number(next.validationRequired) === 1}
                                >
                                    <option value="">ft² / m²</option>
                                    <option value="Sq Feet">ft²</option>
                                    <option value="Sq Meter">m²</option>
                                </select>
                            </div>
                        </div>
                    );
                    i += 2;
                    continue;
                }
            }

            if (name === 'LotSizeArea') {
                const next = fields[i + 1];
                if (next?.field_name === 'LotSizeUnits') {
                    const label = formatFieldLabel(f.field_name_label || 'LotSizeArea');
                    out.push(
                        <div key={`pair-lot-${i}`} className="add-listing-field-block">
                            <span className="add-listing-field-label">
                                {label}
                                {required && <span className="add-listing-required-star">*</span>}
                            </span>
                            <div className="add-listing-inline-row">
                                <input
                                    type="text"
                                    className="add-listing-input add-listing-input--grow"
                                    placeholder={formatFieldLabel(f.field_name_label || 'LotSizeArea')}
                                    value={fieldValues.LotSizeArea ?? ''}
                                    onChange={(e) => setField('LotSizeArea', e.target.value)}
                                    required={required}
                                />
                                <select
                                    className="add-listing-input add-listing-unit-select"
                                    value={fieldValues.LotSizeUnits ?? ''}
                                    onChange={(e) => setField('LotSizeUnits', e.target.value)}
                                    aria-label={formatFieldLabel(next.field_name_label || 'Unit')}
                                    required={Number(next.validationRequired) === 1}
                                >
                                    <option value="">ft² / m²</option>
                                    <option value="Sq Feet">ft²</option>
                                    <option value="Sq Meter">m²</option>
                                </select>
                            </div>
                        </div>
                    );
                    i += 2;
                    continue;
                }
            }

            if (name === 'LivingAreaUnits' || name === 'LotSizeUnits') {
                i += 1;
                continue;
            }

            if (name === 'Country') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={addressCountryCode}
                            onChange={(e) => {
                                const code = e.target.value;
                                setAddressCountryCode(code);
                                const item = countryList.find((c) => String(c.code ?? c.id ?? '') === code);
                                const countryName = item ? String(item.name ?? item.country_name ?? '').trim() : '';
                                // For listing_add: send the code in the field, and the name in *_name helper keys.
                                setField('Country', code);
                                setField('country_name', countryName);
                                setAddressStateId('');
                                setAddressCityId('');
                                setField('StateOrProvince', '');
                                setField('state_name', '');
                                setField('City', '');
                                setField('city_name', '');
                                setStateList([]);
                                setCityList([]);
                                if (code) fetchStateAPI(code);
                            }}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {countryList.map((c) => (
                                <option key={String(c.code ?? c.id ?? c.name)} value={String(c.code ?? c.id ?? '')}>
                                    {c.name ?? c.country_name ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'StateOrProvince') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={addressStateId}
                            onChange={(e) => {
                                const sid = e.target.value;
                                setAddressStateId(sid);
                                const item = stateList.find(
                                    (s) => String(s.region_code ?? s.state_id ?? s.id ?? '') === sid
                                );
                                const stateName = item
                                    ? String(item.region_name ?? item.state_name ?? item.name ?? '').trim()
                                    : '';
                                // Listing_add: send code in field, name in helper key.
                                setField('StateOrProvince', sid);
                                setField('state_name', stateName);
                                setAddressCityId('');
                                setField('City', '');
                                setField('city_name', '');
                                setCityList([]);
                                if (sid && addressCountryCode) fetchCityAPI(sid, addressCountryCode);
                            }}
                            disabled={!addressCountryCode}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {stateList.map((s) => (
                                <option
                                    key={String(s.region_code ?? s.state_id ?? s.id ?? s.region_name)}
                                    value={String(s.region_code ?? s.state_id ?? s.id ?? '')}
                                >
                                    {s.region_name ?? s.state_name ?? s.name ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'City') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={addressCityId}
                            onChange={(e) => {
                                const cid = e.target.value;
                                setAddressCityId(cid);
                                const item = cityList.find((c) => String(c.city_id ?? c.id ?? '') === cid);
                                const cityName = item ? String(item.city_name ?? item.name ?? '').trim() : '';
                                // Listing_add: send code in field, name in helper key.
                                setField('City', cid);
                                setField('city_name', cityName);
                            }}
                            disabled={!addressStateId}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {cityList.map((c) => (
                                <option key={String(c.city_id ?? c.id ?? c.city_name)} value={String(c.city_id ?? c.id ?? '')}>
                                    {c.city_name ?? c.name ?? ''}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'StandardStatus') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={fieldValues.StandardStatus ?? ''}
                            onChange={(e) => setField('StandardStatus', e.target.value)}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {STANDARD_STATUS_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'PropertyType') {
                const label = formatFieldLabel(f.field_name_label || name);
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        <select
                            className="add-listing-input"
                            value={fieldValues.PropertyType ?? ''}
                            onChange={(e) => setField('PropertyType', e.target.value)}
                            required={required}
                        >
                            <option value="">{label}</option>
                            {propertyTypeOptions.map((pt) => (
                                <option key={pt.id} value={pt.id}>
                                    {formatPropertyTypeDisplay(pt.type)}
                                </option>
                            ))}
                        </select>
                    </div>
                );
                i += 1;
                continue;
            }

            if (name === 'PublicRemarks') {
                const label = formatFieldLabel(f.field_name_label || 'PublicRemarks');
                out.push(
                    <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block public-remarks-block">
                        <span className="add-listing-field-label">
                            {label}
                            {required && <span className="add-listing-required-star">*</span>}
                        </span>
                        {/* <div className="add-listing-segment add-listing-segment--two" role="group" aria-label="Public remarks mode">
                            <button
                                type="button"
                                className={remarksMode === 'ai' ? 'active' : ''}
                                onClick={() => setRemarksMode('ai')}
                            >
                                AI Generated
                            </button>
                            <button
                                type="button"
                                className={remarksMode === 'manual' ? 'active' : ''}
                                onClick={() => setRemarksMode('manual')}
                            >
                                Manually
                            </button>
                        </div> */}
                        {/* <p className="add-listing-remarks-hint">
                            Click on generate button to get your AI generated description
                        </p> */}
                        <textarea
                            className="add-listing-textarea"
                            placeholder={label}
                            rows={4}
                            value={fieldValues.PublicRemarks ?? ''}
                            onChange={(e) => setField('PublicRemarks', e.target.value)}
                            required={required}
                        />
                        {/* <div className="add-listing-remarks-actions justify-center">
                            <div className="add-listing-remarks-lang-wrap" ref={remarksLangMenuRef}>
                                <button
                                    type="button"
                                    className="add-listing-remarks-btn add-listing-remarks-btn--secondary"
                                    onClick={() => setRemarksLangMenuOpen((o) => !o)}
                                    aria-expanded={remarksLangMenuOpen}
                                    aria-haspopup="listbox"
                                >
                                    Change Language

                                </button>
                                <p className='mt-2'> Selected Language:&nbsp;
                                    <span className="add-listing-remarks-lang-current">
                                        {langArray.find((l) => l.shortform === selectedAiLang)?.longform ?? 'English'}
                                    </span>
                                </p>
                                {remarksLangMenuOpen && (
                                    <ul className="add-listing-remarks-lang-menu" role="listbox">
                                        {langArray.map((lang) => (
                                            <li key={lang.shortform} role="option" aria-selected={selectedAiLang === lang.shortform}>
                                                <button
                                                    type="button"
                                                    className={
                                                        selectedAiLang === lang.shortform
                                                            ? 'add-listing-remarks-lang-item active'
                                                            : 'add-listing-remarks-lang-item'
                                                    }
                                                    onClick={() => {
                                                        setSelectedAiLang(lang.shortform);
                                                        setRemarksLangMenuOpen(false);
                                                    }}
                                                >
                                                    <span className="add-listing-remarks-lang-long">{lang.longform}</span>
                                                    <span className="add-listing-remarks-lang-native">{lang.lanName}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <button
                                type="button"
                                className="add-listing-remarks-btn add-listing-remarks-btn--primary"
                                disabled={aiRemarksLoading}
                                onClick={() => {
                                    setRemarksMode('ai');
                                    aiGeneratedResponse();
                                }}
                            >
                                {aiRemarksLoading ? 'Generating…' : 'Generate'}
                            </button>
                        </div> */}
                    </div>
                );
                i += 1;
                continue;
            }

            const label = formatFieldLabel(f.field_name_label || name);
            out.push(
                <div key={`field-${f.listing_fields_id}-${i}`} className="add-listing-field-block">
                    <span className="add-listing-field-label">
                        {label}
                        {required && <span className="add-listing-required-star">*</span>}
                    </span>
                    <input
                        type="text"
                        className="add-listing-input"
                        placeholder={label}
                        value={fieldValues[name] ?? ''}
                        onChange={(e) => setField(name, e.target.value)}
                        required={required}
                    />
                </div>
            );
            i += 1;
        }

        return out;
    };

    if (!loadData) {
        return (
            <div className="content-right add-listing-page add-listing-loading">
                <p className="add-listing-loading-text">Loading listing fields…</p>
            </div>
        );
    }

    return (
        <div className="content-right add-listing-page">
            <button type="button" className="add-listing-desktop-back" onClick={() => navigate('/home/my-listings')}>
                ← {stringsoflanguages.backToMyListings}
            </button>
            <div className="add-listing-form">


                <div className="add-listing-field-block">
                    <span className="add-listing-field-label">Listing Type</span>
                    <div className="add-listing-segment add-listing-segment--two" role="group" aria-label="Listing type">
                        <button
                            type="button"
                            className={listingType === 'public' ? 'active' : ''}
                            onClick={() => {
                                setListingType('public');
                                setFeedGroup('1');
                            }}
                        >
                            Public
                        </button>
                        <button
                            type="button"
                            className={listingType === 'pocket' ? 'active' : ''}
                            onClick={() => setListingType('pocket')}
                        >
                            Pocket
                        </button>
                    </div>
                </div>

                <div className="add-listing-field-block">
                    <span className="add-listing-field-label">Portfolio Feed Group</span>
                    {listingType === 'pocket' ? (
                        <div className="add-listing-segment add-listing-segment--five" role="group" aria-label="Portfolio feed group">
                            <button type="button" className="active" onClick={() => setFeedGroup('6')}>
                                Pocket Feed
                            </button>
                        </div>
                    ) : (
                        <div className="add-listing-segment add-listing-segment--five" role="group" aria-label="Portfolio feed group">
                            {['1', '2', '3', '4', '5'].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    className={feedGroup === n ? 'active' : ''}
                                    onClick={() => setFeedGroup(n)}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="add-listing-field-block">
                    <span className="add-listing-field-label">Pictures</span>
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
                        multiple
                        disabled={isImageUploading || isOptimizingImages}
                        className="add-listing-file-input"
                        onChange={handleImageChange}
                    />
                    {isMobile && (
                        <input
                            key={cameraInputKey}
                            ref={cameraInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            disabled={isImageUploading || isOptimizingImages}
                            className="add-listing-file-input"
                            onChange={handleCameraImageChange}
                        />
                    )}
                    {isMobile ? (
                        <div className="add-listing-mobile-image-pick" role="group" aria-label="Add pictures">
                            {!mobileImagePickerOpen ? (
                                <button
                                    type="button"
                                    className="add-listing-mobile-add-image-btn"
                                    onClick={() => setMobileImagePickerOpen(true)}
                                    disabled={isImageUploading || isOptimizingImages}
                                >
                                    Add Image
                                </button>
                            ) : (
                                <>
                                    <div className="add-listing-segment add-listing-segment--two add-listing-segment--image-source">
                                        <button
                                            type="button"
                                            onClick={handleAddImageClick}
                                            disabled={isImageUploading || isOptimizingImages}
                                            aria-label="Choose from gallery"
                                        >
                                            Gallery
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleMobileCameraClick}
                                            disabled={isImageUploading || isOptimizingImages}
                                            aria-label="Take photo with camera"
                                        >
                                            Camera
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        className="add-listing-mobile-image-cancel"
                                        onClick={() => setMobileImagePickerOpen(false)}
                                    >
                                        Cancel
                                    </button>
                                    <p className="add-listing-mobile-image-hint">
                                        Gallery or Camera (max {MAX_LISTING_IMAGE_COUNT} images; large files are
                                        compressed to 5MB each).
                                    </p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div
                            role="button"
                            tabIndex={0}
                            aria-busy={isOptimizingImages}
                            className={`add-listing-dropzone${imageDropActive ? ' add-listing-dropzone--active' : ''}${isOptimizingImages ? ' add-listing-dropzone--optimizing' : ''
                                }`}
                            onClick={handleAddImageClick}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleAddImageClick();
                                }
                            }}
                            onDragEnter={handleImageDragOver}
                            onDragOver={handleImageDragOver}
                            onDragLeave={handleImageDragLeave}
                            onDrop={handleImageDrop}
                        >
                            <span className="add-listing-dropzone-icon" aria-hidden>
                                +
                            </span>
                            <span className="add-listing-dropzone-title">
                                {isOptimizingImages ? 'Optimizing images…' : 'Click to upload multiple images'}
                            </span>
                            <span className="add-listing-dropzone-sub">
                                {isOptimizingImages
                                    ? 'Large photos are compressed to under 5 MB each'
                                    : 'or drag and drop images here'}
                            </span>
                            <span className="add-listing-dropzone-meta">
                                Supports: JPG, PNG, GIF, WebP (max {MAX_LISTING_IMAGE_COUNT} images; large files
                                compressed to 5MB each)
                            </span>
                            <span className="add-listing-dropzone-paste">Press Ctrl+V to paste images from clipboard</span>
                        </div>
                    )}
                    {listingImages.length > 0 && (
                        <>
                            <div className="add-listing-image-previews">
                                {listingImages.map((item) => (
                                    <div key={item.url} className="add-listing-thumb-wrap">
                                        <img src={item.url} alt="" className="add-listing-thumb" />
                                        <button
                                            type="button"
                                            className="add-listing-thumb-remove"
                                            onClick={() => removeListingImage(item.url)}
                                            disabled={isImageUploading || isOptimizingImages}
                                            aria-label="Remove image"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>

                        </>
                    )}
                </div>

                <h4 className="add-listing-section-title">Listing Info</h4>

                <div className="detail-content">{listingFieldIds.length > 0 ? renderDynamicFields() : <p>No fields to display.</p>}</div>
            </div>

            <div className="btn-sec flex justify-center gap-4">
                <button type="button" className="cancel-btn custom-btn" onClick={() => navigate('/home/my-listings')}>
                    {stringsoflanguages.cancel}
                </button>
                <button type="button" className="save-btn custom-btn" onClick={() => addListingApi()} disabled={isDataLoading}>
                    {isDataLoading && <span className="add-listing-submit-spinner" aria-hidden />}
                    {isDataLoading ? stringsoflanguages.submitting : stringsoflanguages.submit}
                </button>
            </div>
        </div>
    );
}
